/**
 * Regras CSS "só vídeo" do player da teleconsulta, para a janela PiP e o mini-player (LO-829).
 *
 * Exceção consciente à regra "classes do tema, sem CSS novo nem estilo inline": o design system não cobre
 * "mostrar só o vídeo do player" e o componente do player não pode ser alterado (frente LO-1300). As regras
 * são geradas a partir dos nomes reais do `AgoraVideoContainer.module.css`, sem arquivo de estilo novo.
 *
 * Lista de PERMISSÃO, não de bloqueio: dentro do container do vídeo só ficam visíveis o card do paciente
 * (o filho que contém `remoteUser`), o overlay de espera, o selo de gravação (quando existir) e os botões de
 * microfone e câmera próprios do PiP (`ControlesPip`, os únicos que recebem clique) e, só no PiP, a miniatura
 * do médico com o aviso de câmera desligada (`miniaturaDoMedico`). Todo o resto — barra de
 * controles, miniatura do médico, status, menus de fundo virtual (que não têm classe de módulo) e o que for
 * acrescentado ao player no futuro — fica oculto.
 */

export type ClassesDoPlayer = {
  readonly videoContainer: string
  readonly remoteUser: string
  readonly waitingOverlay: string
  /** Miniatura da câmera do próprio médico (`LocalUser`). */
  readonly localPreview: string
  /** Aviso "câmera desligada" que o player põe no lugar da miniatura. */
  readonly localOff: string
  readonly localOffIcon: string
  /** Só existe a partir da LO-1300 (gravação). */
  readonly recordingBadge?: string
}

export type OpcoesSomenteVideo = {
  /**
   * Mantém a miniatura do médico (só no PiP; no mini-player não cabe). Dá retorno ao botão de câmera da
   * janela: o médico vê o que o paciente está vendo.
   */
  miniaturaDoMedico?: boolean
}

/**
 * Miniatura no PiP: 4:3 como a da tela (180×135), menor para não disputar a área do vídeo do paciente. No
 * canto superior direito — o esquerdo é do selo "Gravando" e o centro inferior, dos controles do PiP.
 */
const MINIATURA_PIP = { largura: 120, altura: 90, margem: 12, icone: 24 }

/**
 * O CRA gera o hash dos CSS modules em base64, que pode conter "+" e "/" (ex.: `videoContainer__kKc+B`).
 * Sem escapar, o seletor fica inválido e o navegador descarta a regra inteira. Escapa tudo que não for
 * letra, dígito, "_" ou "-" (equivale a `CSS.escape` para esses nomes; o jsdom dos testes não tem `CSS.escape`).
 */
const escaparClasse = (nome: string) => nome.replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`)

/**
 * Marca os controles próprios do PiP (microfone e câmera), filhos diretos do container do vídeo. Eles nascem
 * com `d-none` — na tela do atendimento a barra completa já tem esses botões — e só estas regras os exibem.
 */
export const ATRIBUTO_CONTROLES_PIP = 'data-controles-pip'

/** @param escopo Seletor onde as regras valem: `body` no PiP, `#id` do mini-player na aba. */
export const regrasSomenteVideo = (
  classes: ClassesDoPlayer,
  escopo: string,
  { miniaturaDoMedico = false }: OpcoesSomenteVideo = {}
): string => {
  const classe = (nome: string) => `.${escaparClasse(nome)}`
  const container = `${escopo} ${classe(classes.videoContainer)}`
  const controles = `[${ATRIBUTO_CONTROLES_PIP}]`
  const miniatura = classe(classes.localPreview)
  const cameraDesligada = classe(classes.localOff)
  const permitidos = [
    `:not(:has(${classe(classes.remoteUser)}))`,
    `:not(${classe(classes.waitingOverlay)})`,
  ]
  if (classes.recordingBadge) permitidos.push(`:not(${classe(classes.recordingBadge)})`)
  permitidos.push(`:not(${controles})`)
  if (miniaturaDoMedico) permitidos.push(`:not(${miniatura})`, `:not(${cameraDesligada})`)

  const regras = [
    `${container} > *${permitidos.join('')} { display: none !important; }`,
    `${container} { pointer-events: none; }`,
    `${container} > ${controles} { display: flex !important; pointer-events: auto; }`,
    // Prende o player ao container mesmo maximizado: maximizado ele usa `position-fixed` + `z-index: 9999`
    // inline e cobriria a aba inteira (mini-player) ou o cabeçalho com o "Voltar ao atendimento" (PiP).
    `${container} { position: absolute !important; inset: 0 !important; z-index: auto !important; }`,
  ]
  if (miniaturaDoMedico) {
    const { largura, altura, margem, icone } = MINIATURA_PIP
    // `!important`: o módulo do player fixa o tamanho da miniatura com `!important`.
    regras.push(
      `${container} > :is(${miniatura}, ${cameraDesligada}) { top: ${margem}px !important; right: ${margem}px !important; bottom: auto !important; width: ${largura}px !important; height: ${altura}px !important; }`,
      `${container} > ${cameraDesligada} ${classe(
        classes.localOffIcon
      )} { font-size: ${icone}px !important; }`
    )
  }
  return regras.join('\n')
}
